"use client"

import { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useSuccessFailure } from "../context/SuccessFailureContext"
import api from "../api"
import EventHeader from "./EventHeader"
import EventTabs from "./EventTabs"
import EventDetailsTab from "./EventDetailsTab"
import GuestListTab from "./GuestListTab"
import ServiceBidsTab from "./ServiceBidsTab"
import BudgetTracker from "./BudgetTracker"
import LoadingSpinner from "./LoadingSpinner"
import ErrorMessage from "./ErrorMessage"
import EventAnalytics from "../components/EventAnalytics"
import { useAuth } from "../context/AuthContext"
import { Helmet } from 'react-helmet-async'
import config from "../config"
import { formatEventDateTime, formatMoney } from "../utils/format"

const EventDetails = () => {
  const eventTitle = process.env.REACT_APP_EVENT_TITLE || "EventSawa"
  const eventDescription = process.env.REACT_APP_EVENT_DESCRIPTION || "We make events sawa"
  const { id, event_slug } = useParams()
  const navigate = useNavigate()
  const { user, isLoggedIn } = useAuth()
  const { showSuccessModal, showFailureModal } = useSuccessFailure()

  // Main state
  const [event, setEvent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false)
  const [showBudgetEdit, setShowBudgetEdit] = useState(false)
  const [budgetEdit, setBudgetEdit] = useState({
    total_budget: 0,
    remaining_budget: 0,
  })

  // Guest management state
  const [invitations, setInvitations] = useState([])
  const [guests, setGuests] = useState([])

  // Service requests state
  const [serviceRequests, setServiceRequests] = useState([])
  const [selectedTab, setSelectedTab] = useState("details")

  // Fetch service requests - only for authenticated users
  const fetchServiceRequests = useCallback(async () => {
    if (!event_slug || !isLoggedIn) return;

    try {
      const response = await api.get(`/bids/service-requests/${event_slug}/`);
      if (response.status === 200 && response.data) {
        setServiceRequests(response.data);
      }
    } catch (error) {
      // console.error("Error fetching service requests:", error);
      // Don't show error to user for these non-critical features
    }
  }, [event_slug, isLoggedIn]);

  // Fetch invitations and guests - only for authenticated users
  const fetchInvitationsAndGuests = useCallback(async () => {
    if (!isLoggedIn || !event?.id) {
      // console.log('Skipping fetchInvitationsAndGuests - not logged in or no event id');
      return;
    }

    try {
      // These endpoints return a BARE ARRAY (pagination disabled for mobile), but
      // older code expected a paginated {results, next} object. Handle both shapes.
      const pageItems = (data) => Array.isArray(data) ? data : (data?.results || []);
      const nextOf = (data) => Array.isArray(data) ? null : (data?.next || null);

      // Get all pages of invitations
      let allInvitations = [];
      let nextUrl = `/invitations/?event_id=${event.id}&page=1`;

      while (nextUrl) {
        const response = await api.get(nextUrl).catch(err => {
          // console.error('Error fetching invitations:', err);
          return { data: [] };
        });

        allInvitations = [...allInvitations, ...pageItems(response.data)];
        nextUrl = nextOf(response.data);
      }

      // Get all pages of guests
      let allGuests = [];
      nextUrl = `/invitations/guests/?event_id=${event.id}&page=1`;

      while (nextUrl) {
        const response = await api.get(nextUrl).catch(err => {
          // console.error('Error fetching guests:', err);
          return { data: [] };
        });

        allGuests = [...allGuests, ...pageItems(response.data)];
        nextUrl = nextOf(response.data);
      }

      // console.log('Fetched invitations:', allInvitations);
      // console.log('Fetched guests:', allGuests);

      setInvitations(allInvitations);
      setGuests(allGuests);
    } catch (error) {
      // console.error("Error in fetchInvitationsAndGuests:", error);
    }
  }, [event?.id, isLoggedIn]);

  // Handle event registration
  const handleRegisterForEvent = async () => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }

    try {
      const response = await api.post(
        '/invitations/register-to-attend/',
        { event: event.id }  // Send the event ID in the request body
      );

      if (response.status === 201) {
        showSuccessModal('Your attendance request has been sent to the event organizer!');
        // Refresh event data to update the UI
        fetchEventDetails();
        // Refresh the page to update the UI state
        window.location.reload();
      }
    } catch (error) {
      // console.error('Registration error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to register for the event';
      showFailureModal(errorMessage);
    }
  };

  // Fetch event details
  const fetchEventDetails = useCallback(async () => {
    try {
      setLoading(true);
      // Extract just the slug from the path
      const slug = event_slug.split('/').pop();

      // Create a clean request without auth headers for public access
      const response = await api.get(`/events/${slug}/`, {
        _noAuth: true // This tells our API interceptor to skip auth headers
      });

      // console.log('Event API Response:', response.data);
      if (response.status === 200 && response.data) {
        const eventData = response.data;
        setEvent(eventData);
        setBudgetEdit({
          total_budget: eventData.total_budget || 0,
          remaining_budget: eventData.remaining_budget || 0,
        });
        setError(null); // Clear any previous errors

        // Only fetch protected data if user is logged in
        if (isLoggedIn) {
          await Promise.all([
            fetchServiceRequests(),
            fetchInvitationsAndGuests()
          ]);
        }
      } else {
        setError("Event not found");
      }
    } catch (error) {
      // console.error('Error fetching event details:', error);
      // If it's a 401, it means the event is private and requires login
      if (error.response?.status === 401) {
        navigate('/login', { state: { from: window.location.pathname } });
        return;
      }
      setError(error.response?.data?.detail || "Failed to load event details. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [event_slug, isLoggedIn, navigate, fetchServiceRequests, fetchInvitationsAndGuests]);

  // Handle budget edit
  const handleBudgetEdit = async () => {
    try {
      await api.patch(
        `/events/${event_slug}/`,
        {
          total_budget: Number.parseFloat(budgetEdit.total_budget),
          remaining_budget: Number.parseFloat(budgetEdit.remaining_budget),
        }
      )
      showSuccessModal("Budget updated successfully")
      setShowBudgetEdit(false)
      fetchEventDetails()
    } catch (error) {
      showFailureModal("Failed to update budget")
    }
  }

  // Handle delete
  const handleDelete = async () => {
    try {
      const response = await api.post(
        `/events/${event_slug}/soft_delete/`,
        {}
      )
      showSuccessModal(response.data.message || "Event deleted successfully")
      navigate("/my-events")
    } catch (error) {
      showFailureModal(error.response?.data?.error || "Failed to delete event")
    }
    setShowDeleteConfirm(false)
  }

  // Handle restore
  const handleRestore = async () => {
    try {
      const response = await api.post(
        `/events/${event_slug}/restore/`,
        {}
      )
      showSuccessModal(response.data.message || "Event restored successfully")
      navigate("/my-events")
    } catch (error) {
      showFailureModal(error.response?.data?.error || "Failed to restore event")
    }
    setShowRestoreConfirm(false)
  }

  useEffect(() => {
    let isMounted = true;

    const loadEventData = async () => {
      try {
        if (event_slug) {
          await fetchEventDetails();
        }
      } catch (error) {
        // console.error('Error in event data loading:', error);
        if (isMounted) {
          setError('Failed to load event data. Please try again.');
          setLoading(false);
        }
      }
    };

    loadEventData();

    return () => {
      isMounted = false;
    };
  }, [event_slug, fetchEventDetails]);

  // created_by may be a numeric id, a string, or a nested user object depending on
  // the endpoint — compare loosely so creator-only tabs (Seating, Budget, etc.)
  // don't silently disappear on a "5" === 5 mismatch.
  const createdById = event?.created_by && typeof event.created_by === "object"
    ? event.created_by.id
    : event?.created_by
  const isCreator = user?.id != null && createdById != null &&
    String(user.id) === String(createdById)

  // ---- SEO / social / structured data (international-ready) ----
  const pageUrl = typeof window !== "undefined" ? window.location.href : ""
  const ogImage = event?.thumbnail_photo
    ? (String(event.thumbnail_photo).startsWith("http")
        ? event.thumbnail_photo
        : `${config.MEDIA_BASE_URL}${event.thumbnail_photo}`)
    : ""
  const metaDesc = (event?.description || eventDescription || "").slice(0, 160)
  const when = event ? formatEventDateTime(event.date, event.time, event.timezone) : null
  const jsonLd = event ? {
    "@context": "https://schema.org",
    "@type": "Event",
    name: event.title,
    startDate: when?.iso || undefined,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    location: { "@type": "Place", name: event.location, address: event.location },
    image: ogImage ? [ogImage] : undefined,
    description: event.description,
    organizer: { "@type": "Organization", name: "EventSawa" },
    offers: (event.is_paid_event && Number(event.ticket_price) > 0) ? {
      "@type": "Offer",
      price: Number(event.ticket_price),
      priceCurrency: event.currency || "KES",
      url: pageUrl,
      availability: "https://schema.org/InStock",
    } : undefined,
  } : null

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Helmet>
          <title>{eventTitle}</title>
          <meta name="description" content={eventDescription} />
        </Helmet>
        <div className="flex justify-center items-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Helmet>
          <title>{eventTitle}</title>
          <meta name="description" content={eventDescription} />
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message={error} />
        </div>
      </div>
    )
  }

  // Event not found
  if (!event) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Helmet>
          <title>{eventTitle}</title>
          <meta name="description" content={eventDescription} />
        </Helmet>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ErrorMessage message="Event not found" />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>{`${event.title} | EventSawa`}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="event" />
        <meta property="og:title" content={event.title} />
        <meta property="og:description" content={metaDesc} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="EventSawa" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={event.title} />
        <meta name="twitter:description" content={metaDesc} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Event Header */}
        <EventHeader
          event={event}
          isCreator={isCreator}
          isLoggedIn={isLoggedIn}
          navigate={navigate}
          event_slug={event_slug}
          onDelete={() => setShowDeleteConfirm(true)}
          onRestore={() => setShowRestoreConfirm(true)}
        />

        {/* Navigation Tabs */}
        <EventTabs
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          isCreator={isCreator}
          isLoggedIn={isLoggedIn}
          event={event}
          guests={guests}
          invitations={invitations}
          onRegister={handleRegisterForEvent}
          fetchInvitationsAndGuests={fetchInvitationsAndGuests}
          showSuccessModal={showSuccessModal}
          showFailureModal={showFailureModal}
        />

        {selectedTab === "analytics" && <EventAnalytics event={event} isCreator={isCreator} />}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96">
            <h3 className="text-xl font-bold mb-4 text-red-600">Delete Event</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete this event? This action can be undone later.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96">
            <h3 className="text-xl font-bold mb-4 text-green-600">Restore Event</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">Are you sure you want to restore this event?</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRestoreConfirm(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRestore}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Restore Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Budget Edit Modal */}
      {showBudgetEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96">
            <h3 className="text-xl font-bold mb-4">Edit Budget</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Budget ($)</label>
                <input
                  type="number"
                  value={budgetEdit.total_budget}
                  onChange={(e) =>
                    setBudgetEdit((prev) => ({
                      ...prev,
                      total_budget: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remaining Budget ($)</label>
                <input
                  type="number"
                  value={budgetEdit.remaining_budget}
                  onChange={(e) =>
                    setBudgetEdit((prev) => ({
                      ...prev,
                      remaining_budget: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowBudgetEdit(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBudgetEdit}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default EventDetails
